import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { CREATOR_ROWS, UI_LOGICAL_SIZE, creatorOffer, type Appearance, type Looks } from "odyssey-shared";
import { Window, logical, useLogicalSize, type GlyphName } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { ENTRY_METRICS } from "./entry-screen.tsx";
import { lookWindowBox } from "./look-preview/layout.ts";
import type { LookPreviewSource } from "./look-preview/look-figure.tsx";
import { LookPanel, type LookPanelWords, type LookRefusal } from "./look-preview/look-panel.tsx";
import { loadLookPointer } from "./looks/page-figures.ts";
import { VoiceChoice } from "./voice-choice.tsx";

/**
 * The Look step of creation and Change look at character select (ADR-094 decisions 23, 27 to 29 and notes 124 to
 * 128; the design card `look-step` and `phone-look`, a visual reference): the Look panel of P2.10-T28 with the Voice
 * choice after its rows, in a window placed on the entry backdrop as the card places it, 600 by 328 at its top left
 * share of the free space, never past the page. It has no wordmark and no server strip, as the card draws none.
 * Escape is the footer's Back or Cancel; Enter on a row is the primary action (the panel's rule). The step sends
 * nothing: whoever mounts it holds the look and the voice and acts on the primary action.
 */

/**
 * What the entry screens need to show a look: the page's looks table, the preview's source, loaded once for the
 * page, and whether the pointer is coarse, which makes swatches and turn buttons the touch size.
 */
export interface EntryLooks {
  readonly looks: Looks;
  /** The preview's source, null when the look sheets' pointer did not load; the same promise every time. */
  readonly source: () => Promise<LookPreviewSource | null>;
  readonly touch: boolean;
}

/**
 * The words of the creation's Look step, drafts for the author but the card's title and "Enter the world", which is
 * decision 23's (`account.lookStep.*` in the language files).
 *
 * @returns The panel's words.
 */
export function lookStepWords(): LookPanelWords {
  return { title: say("account.lookStep.title"), back: say("account.lookStep.back"), randomise: say("account.lookStep.randomise"), primary: say("account.lookStep.primary"), saving: say("account.lookStep.saving") };
}

/**
 * The entry screens' looks for a page: the pointer is fetched once, the first time a screen asks, and a pointer that
 * does not load is warned of once and leaves the preview with no figure.
 *
 * @param looks The page's looks table.
 * @param base The asset base the page loads from.
 * @param touch Whether the pointer is coarse.
 * @param load What loads the pointer; `loadLookPointer` unless a test hands in another.
 * @returns The entry looks.
 */
export function entryLooks(looks: Looks, base: string, touch: boolean, load: (base: string) => Promise<LookPreviewSource["manifest"]> = (from) => loadLookPointer(from)): EntryLooks {
  let loading: Promise<LookPreviewSource | null> | null = null;
  return {
    looks,
    touch,
    source: () => {
      loading ??= load(base).then(
        (manifest): LookPreviewSource => ({ looks, manifest, base }),
        (error: unknown) => {
          console.warn(`The look preview has no sheets: ${error instanceof Error ? error.message : String(error)}`);
          return null;
        },
      );
      return loading;
    },
  };
}

/**
 * The preview's source once it has loaded.
 *
 * @param entry The entry looks.
 * @returns The source; null while it loads and when it did not load.
 */
export function useLookSource(entry: EntryLooks): LookPreviewSource | null {
  const [source, setSource] = useState<LookPreviewSource | null>(null);
  useEffect(() => {
    let live = true;
    void entry.source().then((loaded) => {
      if (live) {
        setSource(loaded);
      }
    });
    return () => {
      live = false;
    };
  }, [entry]);
  return source;
}

/**
 * The Look step's props.
 */
export interface LookStepProps {
  readonly entry: EntryLooks;
  readonly words: LookPanelWords;
  readonly look: Appearance;
  readonly voice: string | null;
  /** Whether a request is out: the footer is disabled, the primary button busy and the Voice choice held. */
  readonly saving: boolean;
  /** Whether the primary action is unavailable, as while Change look shows the look and voice that stand. */
  readonly primaryDisabled?: boolean;
  /** A line over the rows, such as the sentence when a voice has no sample. */
  readonly note?: string | null;
  readonly refusal?: LookRefusal | null;
  readonly onLook: (look: Appearance) => void;
  readonly onVoice: (voice: string | null) => void;
  /** Plays the voice picked. */
  readonly onPreview: () => void;
  /** The glyph Back leads with: the chevron at creation, as the card draws it; none for Change look's Cancel. */
  readonly backGlyph?: GlyphName;
  readonly onBack: () => void;
  readonly onPrimary: () => void;
}

/**
 * The Look step on the entry backdrop.
 *
 * @param props See `LookStepProps`.
 * @returns The screen.
 * @throws Error when the look is not one the page's looks table offers.
 * @example
 * <LookStep entry={entry} words={lookStepWords()} backGlyph="chevron-left" look={look} voice={voice} saving={false} onLook={setLook} onVoice={setVoice} onPreview={hear} onBack={back} onPrimary={enter} />
 */
export function LookStep({ entry, words, look, voice, saving, primaryDisabled = false, note = null, refusal = null, backGlyph, onLook, onVoice, onPreview, onBack, onPrimary }: LookStepProps): ReactElement {
  const offer = useMemo(() => creatorOffer(entry.looks), [entry.looks]);
  const source = useLookSource(entry);
  const screen = useRef<HTMLDivElement>(null);
  const area = useLogicalSize(screen, null) ?? UI_LOGICAL_SIZE;
  const box = lookWindowBox(area);

  useEffect(() => {
    screen.current?.querySelector<HTMLElement>("[data-look='turn-left']")?.focus();
  }, []);

  /**
   * Escape is Back, unless a request is out; the key goes no further.
   *
   * @param event The key press anywhere in the step.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Escape") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!saving) {
      onBack();
    }
  };

  const cell = logical(ENTRY_METRICS.backdropCell);
  return (
    <LookPanel
      frame={({ title, footer, children }) => (
        <div ref={screen} className="ot-login" data-login="screen" data-login-mode="look" onKeyDown={onKeyDown} style={{ backgroundSize: `${cell} ${cell}` }}>
          <div className="ot-look-step" data-login="stack" data-look-step="window" style={{ position: "absolute", left: logical(box.left), top: logical(box.top) }}>
            <Window title={title} width={box.width} height={box.height} footer={footer}>
              {children}
            </Window>
          </div>
        </div>
      )}
      words={words}
      rows={CREATOR_ROWS}
      offer={offer}
      look={look}
      onLook={onLook}
      source={source}
      saving={saving}
      primaryDisabled={primaryDisabled}
      note={note}
      refusal={refusal}
      touch={entry.touch}
      after={<VoiceChoice value={voice} disabled={saving} onChange={onVoice} onPreview={onPreview} />}
      {...(backGlyph === undefined ? {} : { backGlyph })}
      onBack={onBack}
      onPrimary={onPrimary}
    />
  );
}
